import {
  formatPaymentPurpose,
  getCountryInfo,
  getTransactionStatusColor,
  toTransactionList,
} from './recipientHelpers';

const IN_TRANSIT_STATUSES = ['Pending', 'Executing', 'Processing'];
const SETTLED_STATUSES = ['Completed'];
const FAILED_STATUSES = ['Failed', 'Cancelled', 'Rejected'];

export function findTransactionByRequestId(transactions, requestId) {
  if (!requestId) return null;
  const list = toTransactionList(transactions);
  return (
    list.find((t) => t.requestId === requestId) ||
    list.find((t) => String(t.requestId || '').startsWith(requestId)) ||
    null
  );
}

export function isIncomingTransaction(transaction) {
  const direction = String(transaction.direction || transaction.flow || transaction.type || '').toLowerCase();
  if (['incoming', 'inbound', 'credit', 'receive', 'received', 'deposit'].some((token) => direction.includes(token))) {
    return true;
  }
  const purpose = String(transaction.paymentPurpose || '').toLowerCase();
  return ['incoming', 'deposit', 'top_up', 'topup', 'credit'].some((token) => purpose.includes(token));
}

export function resolveRecipientForTransaction(transaction, recipients) {
  const txRecipient = transaction?.recipient || {};
  const list = Array.isArray(recipients) ? recipients : [];
  const match = list.find(
    (r) =>
      (txRecipient.id && String(r.id) === String(txRecipient.id)) ||
      (txRecipient.accountId && r.accountId && String(r.accountId) === String(txRecipient.accountId)),
  );
  return match ? { ...txRecipient, ...match } : txRecipient;
}

export function formatRequestReference(requestId) {
  const compact = String(requestId || '').replace(/-/g, '');
  if (!compact) return '—';
  if (compact.length < 8) return `HP-${compact}`;
  return `HP-${compact.slice(0, 4)}-${compact.slice(-4)}`;
}

import { formatNumberWithDecimalLayout, getDecimalLayoutLocale } from './userPreferencesHelpers';

export function formatSignedEuroAmount(amount, { incoming = false, preferences } = {}) {
  const value = Math.abs(Number(amount)) || 0;
  const formatted = formatNumberWithDecimalLayout(value, preferences);
  return `${incoming ? '+' : '−'}${formatted} €`;
}

export function formatForeignAmount(amount, currencyCode, preferences) {
  if (amount == null || !currencyCode) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const locale = getDecimalLayoutLocale(preferences);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${formatNumberWithDecimalLayout(value, preferences)} ${currencyCode}`;
  }
}

export function formatShortDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function getStatusHeroMeta(status) {
  if (SETTLED_STATUSES.includes(status)) {
    return { label: 'Settled', color: 'var(--color-success)' };
  }
  if (IN_TRANSIT_STATUSES.includes(status)) {
    return { label: 'In transit', color: '#1d4ed8' };
  }
  if (FAILED_STATUSES.includes(status)) {
    return { label: 'Failed', color: 'var(--color-error)' };
  }
  return { label: status || '—', color: getTransactionStatusColor(status) };
}

export function formatTransferMethod(recipient, countryCode) {
  const type = String(recipient?.type || '').toLowerCase();
  if (type.includes('swift')) return 'SWIFT';
  if (type.includes('sepa')) return 'SEPA';
  if (type.includes('ach')) return 'ACH';
  if (type.includes('wire')) return 'Wire';
  const code = countryCode || recipient?.countryCode || recipient?.bankCountryCode || recipient?.currency;
  return code ? `Local rail · ${String(code).toUpperCase()}` : 'Local rail';
}

export function getExchangeRateLabel(transaction, preferences) {
  const send = Number(transaction.amountIn);
  const receive = Number(transaction.amountOut);
  if (!send || !receive) return null;
  const rate = receive / send;
  if (!Number.isFinite(rate)) return null;
  const formattedRate = formatNumberWithDecimalLayout(rate, preferences, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
  return `1 ${transaction.baseCurrency} = ${formattedRate} ${transaction.quoteCurrency}`;
}

export function buildTransactionTimeline(transaction) {
  const events = [];
  const { started, finished, status, leg1, leg2 } = transaction;
  const isSettled = SETTLED_STATUSES.includes(status);
  const isFailed = FAILED_STATUSES.includes(status);
  const inTransit = IN_TRANSIT_STATUSES.includes(status);

  if (started) {
    events.push({
      title: 'Transfer created',
      date: started,
      done: true,
    });
  }

  if (leg1?.started) {
    events.push({
      title: 'Currency exchanged',
      date: leg1.started || started,
      done: Boolean(leg1.finished) || isSettled,
    });
  } else if (started) {
    events.push({
      title: 'Funded from balance',
      date: started,
      done: isSettled || Boolean(leg2?.started),
    });
  }

  if (leg2?.started || inTransit || isSettled) {
    events.push({
      title: 'Sent to recipient bank',
      date: leg2?.started || leg1?.finished || started,
      done: Boolean(leg2?.finished) || isSettled,
    });
  }

  if (isFailed) {
    events.push({
      title: 'Transfer failed',
      date: finished || leg2?.finished,
      done: true,
      failed: true,
    });
  } else if (isSettled && finished) {
    events.push({
      title: 'Settled in recipient account',
      date: finished,
      done: true,
    });
  } else if (inTransit) {
    events.push({
      title: 'Settled in recipient account',
      date: null,
      done: false,
    });
  }

  return events;
}

export function getPaymentDocumentName(transaction) {
  const name = transaction?.paymentDocument ?? transaction?.PaymentDocument ?? null;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

export function getDescriptionLine(transaction) {
  if (transaction.paymentReference?.trim()) return transaction.paymentReference.trim();
  const purpose = formatPaymentPurpose(transaction.paymentPurpose);
  return purpose || '—';
}

export function getCountryDisplay(recipient, configCurrencies) {
  const info = getCountryInfo(recipient, configCurrencies);
  const flag = info.flag || '';
  const name = info.name && info.name !== '—' ? info.name : info.code || '—';
  return { ...info, display: flag ? `${flag} ${name}` : name };
}
